import { Link } from "@tanstack/react-router"
import type { ReactNode } from "react"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"

export interface OperatorWorkspaceLink {
  active?: boolean
  description: string
  label: string
  to: string
}

interface OperatorWorkspaceLayoutProps {
  badge?: string
  children: ReactNode
  hideSummarySection?: boolean
  links: readonly OperatorWorkspaceLink[]
}

export function OperatorWorkspaceLayout({
  badge,
  children,
  hideSummarySection = false,
  links,
}: OperatorWorkspaceLayoutProps) {
  const content = (
    <div className="flex flex-col gap-5">
      {hideSummarySection ? null : (
        <>
          {badge ? (
            <div className="flex items-center gap-3 px-1">
              <Badge variant="secondary">{badge}</Badge>
            </div>
          ) : null}
          <nav className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  "shell-panel rounded-4xl border border-border/70 px-4 py-4 transition-colors",
                  link.active
                    ? "border-primary/55 bg-primary/8"
                    : "hover:border-primary/30 hover:bg-background/80"
                )}
              >
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold">{link.label}</span>
                  <span className="text-sm leading-6 text-muted-foreground">
                    {link.description}
                  </span>
                </div>
              </Link>
            ))}
          </nav>
        </>
      )}
      {children}
    </div>
  )

  return (
    <div className="min-h-svh px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">{content}</div>
    </div>
  )
}

interface OperatorPanelProps {
  children: ReactNode
  className?: string
  description?: string
  title: string
}

export function OperatorPanel({
  children,
  className,
  description,
  title,
}: OperatorPanelProps) {
  return (
    <Card className={cn("shell-panel rounded-4xl border-border/70", className)}>
      <CardHeader className="gap-2">
        <CardTitle className="text-xl">{title}</CardTitle>
        {description ? (
          <p className="text-sm leading-6 text-muted-foreground">{description}</p>
        ) : null}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

interface OperatorEmptyStateProps {
  message: string
  title: string
}

export function OperatorEmptyState({
  message,
  title,
}: OperatorEmptyStateProps) {
  return (
    <div className="rounded-3xl border border-dashed border-border/80 bg-background/70 px-5 py-6">
      <p className="font-medium">{title}</p>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">{message}</p>
    </div>
  )
}

interface OperatorMetricCardProps {
  detail: string
  label: string
  value: ReactNode
}

export function OperatorMetricCard({
  detail,
  label,
  value,
}: OperatorMetricCardProps) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="text-sm font-medium text-muted-foreground">{label}</div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
      <div className="mt-2 text-sm leading-6 text-muted-foreground">{detail}</div>
    </div>
  )
}

interface OperatorStatusMessageProps {
  children: ReactNode
  tone?: "danger" | "info" | "warning"
}

export function OperatorStatusMessage({
  children,
  tone = "info",
}: OperatorStatusMessageProps) {
  const toneClass =
    tone === "danger"
      ? "border-destructive/35 bg-destructive/8 text-destructive"
      : tone === "warning"
        ? "border-chart-4/35 bg-chart-4/8 text-foreground"
        : "border-primary/25 bg-primary/6 text-foreground"

  return (
    <div className={cn("rounded-3xl border px-4 py-3 text-sm leading-6", toneClass)}>
      {children}
    </div>
  )
}
